// ENT-3137 WP1 (VETO C1): the canonical list of public, unauthenticated routes
// that carry a bearer access token in their URL query string. Analytics and
// hardening code keys off this list so the definition lives in exactly one
// place. NOTE: the `/share/*` routes also embed tokens, but in the URL *path*
// (`/share/questionnaires/:shareToken`) rather than the query string, so they
// are a different egress class handled by a separate follow-up (see ENT-3137
// WP1 out-of-scope note); do not add them here without a query-string leak.
export const PUBLIC_TOKEN_ROUTE_PATHS = [
  '/telelens/supplier',
  '/telelens/briefing',
  // ENT-4086: the team-invite acceptance routes carry a single-use GoTrue token
  // (`?token_hash=`). Registering them here strips + stashes that token before
  // Sentry Replay / Amplitude can record the URL; the pages recall it via
  // recallPublicToken('token_hash').
  '/team/join',
  '/vendor/team/join',
  // ENT-4311: the scanner-safe password-reset route carries the single-use GoTrue
  // recovery token (`?token_hash=`). Registering it strips + stashes that token
  // before Sentry Replay / Amplitude can record the URL; AuthReset recalls it via
  // recallPublicToken('token_hash'). Loading the page consumes nothing — the token
  // is only spent on the explicit "Set new password" verifyOtp click.
  '/auth/reset',
  // ENT-4312: the scanner-safe signup / email-change confirmation route carries the
  // single-use GoTrue token (`?token_hash=…&type=signup|email_change`). Registering it
  // strips + stashes that token before Sentry Replay / Amplitude can record the URL;
  // AuthConfirm recalls it via recallPublicToken('token_hash'). Loading the page consumes
  // nothing — the token is only spent on the explicit "Confirm email" verifyOtp click.
  '/auth/confirm',
  // ENT-4312 (commit e, absorbs ENT-4315): the supplier-portal password reset lands inert on
  // its own claim-auth surface (`/vendor/claim/auth?token_hash=…&type=recovery`) instead of
  // GoTrue's single-use GET verify endpoint. Registering it strips + stashes the recovery
  // token before Sentry Replay / Amplitude can record the URL; VendorClaimAuth recalls it via
  // recallPublicToken('token_hash') and spends it only on the explicit "Set new password" click.
  '/vendor/claim/auth',
] as const;

const PUBLIC_TOKEN_DYNAMIC_ROUTE_PATTERNS = [
  /^\/share\/customer-briefings\/[^/]+$/,
  /^\/share\/questionnaires\/[^/]+$/,
] as const;

function normalizePathname(pathname: string): string {
  if (typeof pathname !== 'string' || pathname.length === 0) {
    return '';
  }
  // Treat `/telelens/supplier` and `/telelens/supplier/` as the same route.
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed.toLowerCase();
}

/**
 * True when `pathname` is one of the public token-bearing routes. Exact match
 * (trailing slash tolerated); never a prefix match, so an authenticated route
 * that merely starts with `/telelens` is not treated as public.
 */
export function isPublicTokenRoute(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (!normalized) {
    return false;
  }
  return (PUBLIC_TOKEN_ROUTE_PATHS as readonly string[]).includes(normalized)
    || PUBLIC_TOKEN_DYNAMIC_ROUTE_PATTERNS.some((pattern) => pattern.test(normalized));
}
